import classNames from 'classnames'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import { LocalImageUpload } from './local-image-upload'
import { ReactComponent as TrashIcon } from 'svg/fi_trash-2.svg'
import { styles } from 'utils/common'
import { useUploadFile } from 'hooks/query/s3/useUploadFile'
import { toast } from 'react-toastify'
import useDeleteFile from 'hooks/query/s3/useDeleteFile'
import { UpdateTag } from 'components/tag/UpdateTag'
import { v4 as uuidv4 } from 'uuid'
interface ImageUploadBoxProps {
  isSuccess: boolean
  images: string[]
  selectedImages: boolean[]
  setSelectedImages: Dispatch<SetStateAction<boolean[]>>
  selectedUploadImages: boolean[]
  setSelectedUploadImages: Dispatch<SetStateAction<boolean[]>>
  selectedOrder: number[]
  setSelectedOrder: Dispatch<SetStateAction<number[]>>
  uploadImages: {
    file: File
    preview: string // createObjectURL 결과
    id: number
    uploading: boolean
  }[]
  setUploadImages: Dispatch<
    SetStateAction<
      {
        file: File
        preview: string
        uploading: boolean
        id: number
      }[]
    >
  >
}

export const ImageUploadBox = ({
  isSuccess,
  images,
  selectedImages,
  setSelectedImages,
  selectedUploadImages,
  setSelectedUploadImages,
  uploadImages,
  setUploadImages,
  selectedOrder,
  setSelectedOrder,
}: PropsWithChildren<ImageUploadBoxProps>) => {
  const { mutateAsync: uploadFileMutate, isPending } = useUploadFile()
  const { mutateAsync: deleteMutate } = useDeleteFile()

  const handleCheckboxChange = (index: number) => {
    const uploadImgCnt = uploadImages.length ?? 0
    const loadImageCnt = selectedOrder.length ?? 0
    const isAdd = 8 - uploadImgCnt - loadImageCnt > 0

    setSelectedOrder((prev) => {
      if (prev.includes(index)) {
        // 선택 해제: 제거 + 순서 보정
        return prev.filter((i) => i !== index)
      } else if (isAdd) {
        // 새 선택: 뒤에 추가
        return [...prev, index]
      } else {
        return prev
      }
    })
  }

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    document.getElementById('file-input')!.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return
    const MAX_FILE_SIZE_MB = 5
    const selectedFiles = Array.from(event.target.files)
    const originalFile = selectedFiles[0]

    // let safeFile: File
    // try {
    //   const buffer = await originalFile.arrayBuffer()
    //   const fileExt = originalFile.name.split('.').pop()
    //   const uuidFileName = `${uuidv4()}.${fileExt}`
    //   const blob = new Blob([buffer], { type: originalFile.type })
    //   safeFile = new File([blob], uuidFileName, { type: originalFile.type })
    // } catch (err) {
    //   toast.error('파일을 읽을 수 없습니다. 다시 선택해주세요.')
    //   console.error(err)
    //   return
    // }

    const fileSizeMB = originalFile.size / (1024 * 1024)
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `❌ ${originalFile.name}은(는) ${MAX_FILE_SIZE_MB}MB 초과 파일입니다.`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      return
    }

    const tempId = Date.now() + Math.random()
    const tempPreview = URL.createObjectURL(originalFile)
    setUploadImages((prev) => [
      ...prev,
      {
        file: originalFile,
        preview: tempPreview,
        uploading: true,
        id: tempId,
      },
    ])

    try {
      const { fileUrl } = await uploadFileMutate(originalFile)

      setUploadImages((prev) =>
        prev.map((item) =>
          item.id === tempId
            ? {
                ...item,
                preview: fileUrl,
                uploading: false,
              }
            : item
        )
      )
    } catch (error) {
      toast.error(
        `❌ ${originalFile.name}은(는) upload에 실패했습니다. 다시 시도해주세요!`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      console.log(error)
    } finally {
      URL.revokeObjectURL(tempPreview)
      event.target.value = ''
    }
  }
  const getFileKey = (file: File) =>
    `${file.name}_${file.size}_${file.lastModified}`
  const handleDeleteFile = (targetFile: File, targetUrl: string) => {
    const targetKey = getFileKey(targetFile)
    deleteMutate(targetUrl)
    setUploadImages((prev) =>
      prev.filter((imgObj) => imgObj.preview !== targetUrl)
    )

    setSelectedUploadImages((prev) => {
      const targetIndex = uploadImages.findIndex(
        (imgObj) => getFileKey(imgObj.file) === targetKey
      )
      if (targetIndex === -1) return prev
      const newSelected = [...prev]
      newSelected.splice(targetIndex, 1)
      return newSelected
    })
  }

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="flex flex-col w-[343px] md:w-full ml-4 md:ml-0">
        <div className="mb-4">
          <label className="md:ml-0 font-bold text-[#2E343D] text-[24px] flex items-start gap-2">
            이미지 선택{' '}
            {(selectedOrder.length ?? 0) + (uploadImages?.length ?? 0)}/8
            <UpdateTag />
          </label>
          <p className="text-main text-[14px]">
            각 이미지는 배치된 순서 대로 약 2초씩 플레이 되며, 영상이 끝날
            때까지 순서 대로 반복됩니다.
          </p>
          <p className="text-error text-[14px]">
            불러온 이미지 + 업로드한 이미지 = 1개 ~ 8개
          </p>
        </div>
        {isSuccess && (
          <div
            style={{
              background: '#F6F8FB',
              borderRadius: '10px',
              padding: '20px',
            }}
            className="custom-scroll overflow-y-scroll max-h-[460px]"
          >
            <div className="text-[16px]  mb-1">
              <span className="font-bold">업로드한 이미지 </span>{' '}
              <span className="text-[#8F929B] text-[16px] text-sm">
                (각 5MB | jpg, jpeg, png)
              </span>
            </div>
            <div className="flex items-center space-x-2 mb-3"></div>
            <div
              className={classNames('mb-5', {
                'flex gap-1 overflow-x-auto ': isMobile,
                'grid grid-cols-8 gap-1   ': !isMobile,
              })}
            >
              {/* local image upload 슬롯 */}
              {uploadImages.map((el, index) => (
                <div
                  className={classNames('bg-black shrink-0', {
                    'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center':
                      isMobile,
                    'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center ':
                      !isMobile,
                  })}
                >
                  {' '}
                  {el.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                      <svg
                        className="w-6 h-6 text-white animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                        />
                      </svg>
                    </div>
                  )}
                  <img
                    src={el.preview}
                    alt={`img_${index + 1}`}
                    style={styles.image}
                  />
                  <div className="absolute flex items-center cursor-pointer right-1 top-1 z-50 active:scale-95">
                    <div
                      className={
                        'w-[28px] h-[28px] rounded-full border-[1px] bg-[#2E343D] flex items-center justify-center '
                      }
                    >
                      <button
                        className="w-[24px] h-[24px] bg-[#2E343D] rounded-full text-white flex items-center justify-center font-bold "
                        type="button"
                        onClick={() => {
                          handleDeleteFile(el.file, el.preview)
                        }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                  <div className="absolute flex items-center cursor-pointer right-1 bottom-1 z-50 active:scale-95">
                    <div
                      className={
                        'w-[28px] h-[28px] rounded-full border-2 bg-white flex items-center justify-center  border-href'
                      }
                    >
                      <div className="w-[22px] h-[22px] bg-href rounded-full text-white flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* local image 빈 슬롯 */}
              {new Array(8 - selectedOrder.length - (uploadImages.length ?? 0))
                .fill(1)
                .map((src, index) => (
                  <div key={index} className="cursor-pointer">
                    <div>
                      <LocalImageUpload
                        handleButtonClick={handleButtonClick}
                        index={index}
                        isPending={isPending}
                      />
                      <input
                        id="file-input"
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>
                  </div>
                ))}
            </div>
            <hr />
            <div className="mt-3">
              {images.length === 0 ? (
                <p className="text-error text-[14px] mb-5">
                  입력하신 컨텐츠 링크에 사용할 수 있는 이미지가 없습니다.
                  (이미지가 없는 경우, 영상만 있는 경우 등)
                  <br />
                  이미지를 직접 추가해서 컨텐츠 영상을 만들어보세요!
                </p>
              ) : (
                <div>
                  <div className="text-[16px] mb-3  ">
                    <span className="font-bold">불러온 이미지 </span>
                  </div>

                  <div
                    className={classNames('mb-5', {
                      'flex gap-1 overflow-x-auto': isMobile,
                      'grid grid-cols-8 gap-1   ': !isMobile,
                    })}
                  >
                    {images.map((src, index) => {
                      const isChecked = selectedOrder.includes(index)
                      const uploadImageCnt = uploadImages.length ?? 0
                      const order =
                        selectedOrder.indexOf(index) + 1 + uploadImageCnt

                      return (
                        <label
                          key={index}
                          className=" relative w-[85px] cursor-pointer"
                          htmlFor={`selected_img_${index}`}
                        >
                          <div
                            className={classNames('bg-black', {
                              'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center':
                                isMobile,
                              'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center ':
                                !isMobile,
                            })}
                          >
                            <img
                              src={
                                typeof src === 'string'
                                  ? src.includes('pstatic')
                                    ? `${process.env.REACT_APP_API_URL}/crawling/proxy-image?url=${src}`
                                    : src
                                  : URL.createObjectURL(src)
                              }
                              alt={`img_${index + 1}`}
                              style={styles.image}
                            />
                          </div>

                          <div className="absolute flex items-center cursor-pointer right-1 bottom-1">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleCheckboxChange(index)}
                              className="sr-only"
                              aria-label={`이미지 ${index + 1} 선택`}
                              id={`selected_img_${index}`}
                            />
                            <div
                              className={`w-[28px] h-[28px] rounded-full border-2 bg-href flex items-center justify-center ${
                                isChecked ? 'bg-white border-href' : 'bg-white'
                              }`}
                            >
                              {isChecked && (
                                <div className="w-[22px] h-[22px] bg-href rounded-full text-white flex items-center justify-center font-bold">
                                  {order}
                                </div>
                              )}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
